import React from "react";

/**
 * Hotel picker for the trip page, replacing a native select.
 *
 * A native option can only hold one line of unstyled text, which is why the price had to be
 * squeezed into brackets after the name and "sold out" was a red-text hack. Here each hotel gets a
 * row: name, what it does to the price, and what it includes.
 *
 * It is a real listbox, not a div with a click handler - customers reach this on phones and with
 * keyboards, so arrow keys, Enter, Escape and focus return all have to work.
 *
 * With groupByStayType, hotels sit under a heading per stay type, each with an info icon that opens
 * the type's description in place - a hover tooltip would never show on a phone.
 */
export default function HotelOptionSelect({
  options,
  value,
  onChange,
  placeholder = "Select hotel",
  disabled = false,
  loading = false,
  ariaLabel,
  formatAmount,
  groupByStayType = false,
}) {
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [infoOpenFor, setInfoOpenFor] = React.useState(null);
  const containerRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  const groups = groupByStayType ? stayTypeGroups(options) : null;
  // Arrow keys have to walk the list in the order it is drawn, headings or not.
  const ordered = groups ? groups.flatMap((group) => group.options) : options;
  const selectable = ordered.filter((option) => !option.disabled);
  const selected = options.find((option) => String(option.value) === String(value)) || null;

  const close = React.useCallback((returnFocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (returnFocus && buttonRef.current) buttonRef.current.focus();
  }, []);

  // Clicking anywhere else closes the list. Without this the panel survives a click on another
  // night's picker and two lists sit open at once.
  React.useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  React.useEffect(() => {
    if (!open) setInfoOpenFor(null);
  }, [open]);

  const choose = (option) => {
    if (!option || option.disabled) return;
    onChange(String(option.value));
    close();
  };

  const moveActive = (delta) => {
    if (!selectable.length) return;
    const currentIndex = activeIndex >= 0
      ? activeIndex
      : Math.max(0, selectable.findIndex((option) => String(option.value) === String(value)));
    const next = (currentIndex + delta + selectable.length) % selectable.length;
    setActiveIndex(next);
  };

  const onKeyDown = (event) => {
    if (disabled) return;
    if (!open && (event.key === "Enter" || event.key === " " || event.key === "ArrowDown")) {
      event.preventDefault();
      setOpen(true);
      moveActive(0);
      return;
    }
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(selectable[activeIndex]);
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  const differenceLabel = (option) => {
    const price = option.price;
    if (!price) return null;
    if (price.unavailable || price.perPersonPrice == null) {
      return { text: "Price on request", tone: "muted" };
    }
    if (price.selected) return { text: "Current choice", tone: "current" };
    const difference = Number(price.perPersonDifference ?? 0);
    if (!Number.isFinite(difference) || Math.round(difference) === 0) {
      return { text: "Same price", tone: "same" };
    }
    return difference < 0
      ? { text: `Rs. ${formatAmount(Math.abs(difference))} less pp`, tone: "cheaper" }
      : { text: `Rs. ${formatAmount(difference)} more pp`, tone: "dearer" };
  };

  // "2 x Couple Room · Rs. 2,000 less in total" - per person alone hides what the group actually gets.
  const roomsLine = (option) => {
    const price = option.price;
    if (!price || price.unavailable) return "";
    const parts = [];
    const rooms = option.roomsText || price.roomSummary;
    if (rooms) parts.push(rooms);
    const totalDifference = Number(option.totalDifference);
    if (!price.selected && Number.isFinite(totalDifference) && Math.round(totalDifference) !== 0) {
      parts.push(totalDifference < 0
        ? `Rs. ${formatAmount(Math.abs(totalDifference))} less in total`
        : `Rs. ${formatAmount(totalDifference)} more in total`);
    }
    return parts.join(" · ");
  };

  return (
    <div className="hotel-select" ref={containerRef}>
      <button
        type="button"
        ref={buttonRef}
        className="hotel-select__control"
        onClick={() => (disabled ? null : setOpen((current) => !current))}
        onKeyDown={onKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="hotel-select__value">
          {selected ? (
            <>
              <span className="hotel-select__value-name">{selected.label}</span>
              {/* What was picked stays readable once the list closes - not just a name. */}
              {(selected.mealPlan || selected.roomsText) && (
                <span className="hotel-select__value-meta">
                  {selected.mealPlan && (
                    <span className={`hotel-select__chip is-${selected.mealPlan.tone}`}>{selected.mealPlan.label}</span>
                  )}
                  {selected.roomsText && <span>{selected.roomsText}</span>}
                </span>
              )}
            </>
          ) : (
            <span className="text-muted">{placeholder}</span>
          )}
        </span>
        {loading && <span className="spinner-border spinner-border-sm text-muted me-2" aria-hidden="true" />}
        <span className="hotel-select__caret" aria-hidden="true" />
      </button>

      {open && (
        <ul className="hotel-select__menu" role="listbox" aria-label={ariaLabel}>
          {groups
            ? groups.map((group) => (
                <React.Fragment key={`group-${group.key}`}>
                  <li role="presentation" className="hotel-select__group">
                    <span>{group.label}</span>
                    {group.description && (
                      <button
                        type="button"
                        className="hotel-select__info"
                        title={group.description}
                        aria-label={`What is ${group.label}?`}
                        aria-expanded={infoOpenFor === group.key}
                        // Keeps focus on the control, so the arrow keys still work afterwards.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => setInfoOpenFor((current) => (current === group.key ? null : group.key))}
                      >
                        <i className="bi bi-info-circle" aria-hidden="true" />
                      </button>
                    )}
                  </li>
                  {infoOpenFor === group.key && (
                    <li role="presentation" className="hotel-select__group-info">{group.description}</li>
                  )}
                  {group.options.map(renderOption)}
                </React.Fragment>
              ))
            : options.map(renderOption)}
          {!options.length && (
            <li className="hotel-select__option is-disabled">No stays match the price range or filters</li>
          )}
        </ul>
      )}
    </div>
  );

  function renderOption(option) {
    const difference = differenceLabel(option);
    const isSelected = String(option.value) === String(value);
    const activeOption = selectable[activeIndex];
    const isActive = activeOption && String(activeOption.value) === String(option.value);
    // Under a heading the type chip would only repeat it.
    const typeChip = groups ? "" : option.stayType;
    return (
      <li
        key={option.value}
        role="option"
        aria-selected={isSelected}
        aria-disabled={option.disabled || undefined}
        className={[
          "hotel-select__option",
          option.disabled ? "is-disabled" : "",
          isActive ? "is-active" : "",
          isSelected ? "is-selected" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onMouseEnter={() => {
          const index = selectable.findIndex((item) => String(item.value) === String(option.value));
          if (index >= 0) setActiveIndex(index);
        }}
        onClick={() => choose(option)}
      >
        <div className="hotel-select__row">
          <span className="hotel-select__name">{option.label}</span>
          {option.disabled ? (
            <span className="hotel-select__badge is-soldout">{option.disabledReason || "Sold out"}</span>
          ) : (
            difference && (
              <span className={`hotel-select__badge is-${difference.tone}`}>{difference.text}</span>
            )
          )}
        </div>
        {roomsLine(option) && <div className="hotel-select__rooms">{roomsLine(option)}</div>}
        {(option.price?.cheapest || option.mealPlan || option.iotripsPick || typeChip || (option.chips || []).length > 0 || (option.extras || []).length > 0) && (
          <div className="hotel-select__meta">
            {option.iotripsPick && !option.disabled && (
              <span className="hotel-select__chip is-pick">★ Iotrips pick</span>
            )}
            {option.price?.cheapest && !option.disabled && (
              <span className="hotel-select__chip is-lowest">Lowest price</span>
            )}
            {typeChip && <span className="hotel-select__chip is-type">{typeChip}</span>}
            {!option.disabled && option.roomsLeft > 0 && option.roomsLeft <= 2 && (
              <span className="hotel-select__chip is-scarce">
                Only {option.roomsLeft} room{option.roomsLeft === 1 ? "" : "s"} left
              </span>
            )}
            {option.mealPlan && (
              <span className={`hotel-select__chip is-${option.mealPlan.tone}`}>{option.mealPlan.label}</span>
            )}
            {(option.chips || []).map((chip) => (
              <span className="hotel-select__chip" key={chip}>
                {chip}
              </span>
            ))}
            {(option.extras || []).map((extra) => (
              <span className="hotel-select__chip is-extra" key={`extra-${extra}`} title="Available at extra cost">
                {extra}
              </span>
            ))}
          </div>
        )}
        {option.mealNote && !option.disabled && (
          <div className="hotel-select__meal-note">{option.mealNote}</div>
        )}
      </li>
    );
  }
}

// Hotels arrive cheapest first, so taking the types in the order they first appear puts the group
// with the cheapest stay on top. Stays without a type go last. Headings only help once at least one
// stay has a type - a list that is nothing but "Other stays" gets none.
function stayTypeGroups(options) {
  if (!options.some((option) => String(option.stayType || "").trim())) return null;
  const byType = new Map();
  options.forEach((option) => {
    const label = String(option.stayType || "").trim();
    const key = label.toLowerCase();
    if (!byType.has(key)) {
      byType.set(key, { key: key || "other", label: label || "Other stays", description: "", options: [] });
    }
    const group = byType.get(key);
    if (!group.description && option.stayTypeDescription) group.description = option.stayTypeDescription;
    group.options.push(option);
  });
  const untyped = byType.get("");
  byType.delete("");
  const groups = Array.from(byType.values());
  if (untyped) groups.push(untyped);
  return groups;
}
